/**
 * DiskGelo — AAAwards landing orchestrator (premium, no mini-games).
 *
 * Boot sequence:
 *   1. Detect env (touch, reduced-motion, mobile, DPR)
 *   2. Mount background canvas (sky, aurora, mountains, ice chunks, mist, puddles, bokeh, snow)
 *   3. Populate static sections (products, marquee, cta, contact, footer)
 *   4. Wire reveal-on-scroll + counters + top-bar scroll state
 *   5. Start hero timeline (awaits fonts.ready)
 *   6. Mount premium cursor (non-touch only)
 *   7. Hide loader
 */
import './styles.css'
import { gsap } from 'gsap'
import { detectEnv } from './env'
import { mountBackground } from './bg/background'
import { mountHero } from './sections/hero'
import { mountProducts } from './sections/products'
import { mountMarquee } from './sections/marquee'
import { mountCtaBlock } from './sections/cta'
import { mountContact } from './sections/contact'
import { mountFooter } from './sections/footer'
import { mountRevealOnScroll } from './ui/revealOnScroll'
import { mountCounters } from './ui/counter'
import { mountCursor } from './ui/cursor'

function mountTopBarScroll(): void {
  const bar = document.getElementById('top-bar')
  if (!bar) return
  const onScroll = () => {
    if (window.scrollY > 20) bar.classList.add('scrolled')
    else bar.classList.remove('scrolled')
  }
  window.addEventListener('scroll', onScroll, { passive: true })
  onScroll()
}

function hideLoader(): void {
  const loader = document.getElementById('loader')
  if (!loader) return
  gsap.to(loader, {
    opacity: 0,
    duration: 0.5,
    delay: 0.2,
    ease: 'power2.out',
    onComplete: () => loader.remove(),
  })
}

function boot(): void {
  const env = detectEnv()

  gsap.context(() => {
    // 1. Background canvas loop (mouse-parallax mountains + layers)
    mountBackground(env)

    // 2. Static sections
    mountProducts()
    mountMarquee()
    mountCtaBlock()
    mountContact()
    mountFooter()

    // 3. UI behaviour
    mountTopBarScroll()
    mountRevealOnScroll()
    mountCounters()

    // 4. Hero timeline (async, awaits fonts inside)
    void mountHero(env)

    // 5. Premium cursor (skips itself on touch)
    mountCursor(env)
  }, document.body)

  // 6. Hide loader once everything's mounted
  if (document.readyState === 'complete') {
    hideLoader()
  } else {
    window.addEventListener('load', hideLoader)
  }
}

boot()
